import type { AgentDto } from '@/types/gtm-agents';
import { useAuth } from '@clerk/react';
import { useEffect, useMemo, useRef } from 'react';
import type { SidebarLayoutState } from './use-sidebar-layout.hook';

type FavoriteAgent = Pick<AgentDto, 'id' | 'isFavorite' | 'archivedAt'>;

/**
 * Starring an agent pins it and unstarring it unpins it, as the sidebar
 * showed favorite agents before it had a layout. Only a star the user
 * changes while the sidebar is up counts: the agents' first read and a
 * switch of organization change nothing.
 *
 * Switching organization does not read the agents again, so the list on hand
 * then is still the old organization's. The first list read after the switch
 * is the new organization's starting point, not a change of stars.
 */
export function useFavoriteAgentPins(
  agents: readonly FavoriteAgent[] | undefined,
  { pin, unpin }: Pick<SidebarLayoutState, 'pin' | 'unpin'>
): void {
  const { orgId } = useAuth();
  // As one string, so a refetch with the same favorites changes nothing.
  const favorites = useMemo(
    () =>
      agents
        ? agents
            .filter((agent) => agent.isFavorite && !agent.archivedAt)
            .map((agent) => agent.id)
            .sort()
            .join(',')
        : null,
    [agents]
  );
  const seen = useRef({ orgId, agents, favorites, awaitingList: false });
  useEffect(() => {
    const before = seen.current;
    const listChanged = agents !== before.agents;
    if (before.orgId !== orgId) {
      // A list that came with the switch is the new organization's; one that
      // did not is the old organization's, so wait for the next read.
      seen.current = {
        orgId,
        agents,
        favorites: listChanged ? favorites : null,
        awaitingList: !listChanged,
      };
      return;
    }
    if (before.awaitingList && !listChanged) return;
    seen.current = { orgId, agents, favorites, awaitingList: false };
    if (before.awaitingList) return;
    if (before.favorites === null || favorites === null) return;
    if (before.favorites === favorites) return;
    const was = new Set(before.favorites.split(',').filter(Boolean));
    const now = new Set(favorites.split(',').filter(Boolean));
    for (const id of now) if (!was.has(id)) pin({ kind: 'agent', id });
    for (const id of was) if (!now.has(id)) unpin({ kind: 'agent', id });
  }, [agents, favorites, orgId, pin, unpin]);
}
